import {ArrowRight} from 'lucide-react'
import {CardHelp} from '../../shared/components/CardHelp'
import {StatusType} from '../../shared/types/inventory'

interface StatusDistributionProps {
  values: {status: StatusType; label: string; value: number; color: string}[]
  onSelect: (statuses: StatusType[], label: string) => void
}

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR')
const PERCENTAGE_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 1,
})

export const StatusDistribution = ({values, onSelect}: StatusDistributionProps) => {
  const total = values.reduce((sum, item) => sum + item.value, 0)
  const attentionTotal = values.slice(0, 3).reduce((sum, item) => sum + item.value, 0)
  let offset = 0
  const segments = values
    .filter((item) => item.value > 0)
    .map((item) => {
      const percentage = total ? (item.value / total) * 100 : 0
      const segment = {...item, percentage, offset}
      offset += percentage
      return segment
    })

  const formatPercentage = (percentage: number) => {
    if (percentage > 0 && percentage < 1) return '<1%'
    return `${PERCENTAGE_FORMATTER.format(percentage)}%`
  }

  return (
    <section className='grid gap-4 lg:grid-cols-2'>
      <article className='relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6'>
        <CardHelp
          content='Mostra a proporção de produtos em cada status de estoque dentro do filtro atual.'
          className='absolute right-3 top-3'
        />
        <div className='pr-8'>
          <p className='text-xs font-semibold uppercase tracking-[0.16em] text-teal-600'>
            Panorama geral
          </p>
          <h3 className='mt-1 text-lg font-bold text-slate-900'>Distribuição por status</h3>
          <p className='mt-1 text-xs text-slate-500'>Participação de cada faixa no filtro atual.</p>
        </div>

        <div className='mt-5 flex flex-1 flex-col items-center justify-center'>
          <div className='relative h-52 w-52 sm:h-56 sm:w-56'>
            <svg
              viewBox='0 0 120 120'
              className='h-full w-full -rotate-90 drop-shadow-sm'
              role='img'
              aria-label={`Distribuição de ${NUMBER_FORMATTER.format(total)} produtos por status`}
            >
              <circle cx='60' cy='60' r='48' fill='none' stroke='#e2e8f0' strokeWidth='18' />
              {segments.map((segment) => (
                <circle
                  key={segment.label}
                  cx='60'
                  cy='60'
                  r='48'
                  fill='none'
                  pathLength='100'
                  stroke={segment.color}
                  strokeWidth='18'
                  strokeDasharray={`${segment.percentage} ${100 - segment.percentage}`}
                  strokeDashoffset={-segment.offset}
                  className='transition-all duration-500'
                >
                  <title>
                    {`${segment.label}: ${NUMBER_FORMATTER.format(
                      segment.value
                    )} (${formatPercentage(segment.percentage)})`}
                  </title>
                </circle>
              ))}
            </svg>
            <div className='absolute inset-[18%] flex flex-col items-center justify-center rounded-full bg-white text-center shadow-inner'>
              <strong className='text-2xl font-black tabular-nums text-slate-900 sm:text-3xl'>
                {NUMBER_FORMATTER.format(total)}
              </strong>
              <span className='mt-0.5 text-xs font-medium text-slate-500'>produtos</span>
            </div>
          </div>

          <div className='mt-5 grid w-full max-w-sm grid-cols-2 gap-3'>
            <button
              type='button'
              onClick={() =>
                onSelect(
                  values.slice(0, 3).map((item) => item.status),
                  'Precisam atenção'
                )
              }
              className='group rounded-xl bg-red-50 px-3 py-2.5 text-center transition hover:-translate-y-0.5 hover:bg-red-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-red-300'
            >
              <span className='flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-500'>
                Precisam atenção
                <ArrowRight className='h-3 w-3 transition-transform group-hover:translate-x-0.5' />
              </span>
              <strong className='mt-0.5 block text-lg font-black tabular-nums text-red-700'>
                {NUMBER_FORMATTER.format(attentionTotal)}
              </strong>
            </button>
            <button
              type='button'
              onClick={() =>
                onSelect(
                  values.slice(3).map((item) => item.status),
                  'Demais situações'
                )
              }
              className='group rounded-xl bg-slate-100 px-3 py-2.5 text-center transition hover:-translate-y-0.5 hover:bg-slate-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300'
            >
              <span className='flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500'>
                Demais situações
                <ArrowRight className='h-3 w-3 transition-transform group-hover:translate-x-0.5' />
              </span>
              <strong className='mt-0.5 block text-lg font-black tabular-nums text-slate-700'>
                {NUMBER_FORMATTER.format(total - attentionTotal)}
              </strong>
            </button>
          </div>
        </div>
      </article>

      <article className='h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6'>
        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-blue-600'>
          Detalhamento
        </p>
        <h3 className='mt-1 text-lg font-bold text-slate-900'>Legenda dos status</h3>
        <p className='mt-1 text-xs text-slate-500'>
          Quantidade e participação no total monitorado.
        </p>

        <div className='mt-5 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-100 bg-slate-50/60'>
          {values.map((item) => {
            const percentage = total ? (item.value / total) * 100 : 0
            return (
              <button
                type='button'
                key={item.status}
                onClick={() => onSelect([item.status], item.label)}
                className='group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal-300'
                aria-label={`Ver ${NUMBER_FORMATTER.format(item.value)} produtos com status ${
                  item.label
                }`}
              >
                <div className='flex min-w-0 items-center gap-3'>
                  <span
                    className='h-3 w-3 flex-none rounded-full shadow-sm'
                    style={{backgroundColor: item.color}}
                  />
                  <span className='truncate text-sm font-semibold text-slate-700'>
                    {item.label}
                  </span>
                </div>
                <div className='flex flex-none items-center gap-3'>
                  <strong className='text-sm font-black tabular-nums text-slate-900'>
                    {NUMBER_FORMATTER.format(item.value)}
                  </strong>
                  <span className='w-14 rounded-full bg-white px-2 py-1 text-center text-[11px] font-semibold tabular-nums text-slate-500 shadow-sm'>
                    {formatPercentage(percentage)}
                  </span>
                  <ArrowRight className='h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-600' />
                </div>
              </button>
            )
          })}
        </div>
      </article>
    </section>
  )
}
